import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export interface CreditRow {
  subscription_id: string;
  package_id: string;
  package_name: string;
  tier_label: string | null;
  audience: string;
  expires_at: string;
  listings_quota: number; listings_used: number;
  projects_quota: number; projects_used: number;
  hot_quota: number; hot_used: number;
  super_hot_quota: number; super_hot_used: number;
  refresh_quota: number; refresh_used: number;
  story_quota: number; story_used: number;
  banner_quota: number; banner_used: number;
  boost_tier: string; boost_quota: number; boost_used: number;
}

const meter = (rows: CreditRow[], qKey: keyof CreditRow, uKey: keyof CreditRow) => {
  const quota = rows.reduce((s, r) => s + (Number(r[qKey]) || 0), 0);
  const used = rows.length ? Math.max(...rows.map((r) => Number(r[uKey]) || 0)) : 0;
  return { quota, used, remaining: Math.max(0, quota - used) };
};

export function useActiveSubscription() {
  const { user } = useAuth();

  const q = useQuery({
    queryKey: ['subscription-credits', user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data } = await supabase.rpc('subscription_credit_summary' as any, { _user_id: user!.id });
      return ((data as any[]) || []) as CreditRow[];
    },
  });

  const rows = q.data || [];
  const primary = rows[0]
    ? {
        subscription_id: rows[0].subscription_id,
        package_id: rows[0].package_id,
        package_name: rows[0].tier_label || rows[0].package_name,
        audience: rows[0].audience,
        expires_at: rows[0].expires_at,
      }
    : null;

  return {
    isLoading: q.isLoading,
    hasActive: rows.length > 0,
    rows,
    primary,
    listings: meter(rows, 'listings_quota', 'listings_used'),
    projects: meter(rows, 'projects_quota', 'projects_used'),
    hot: meter(rows, 'hot_quota', 'hot_used'),
    superHot: meter(rows, 'super_hot_quota', 'super_hot_used'),
    refresh: meter(rows, 'refresh_quota', 'refresh_used'),
    story: meter(rows, 'story_quota', 'story_used'),
    boosts: meter(rows, 'boost_quota', 'boost_used'),
    banners: { ...meter(rows, 'banner_quota', 'banner_used'), totalQuota: meter(rows, 'banner_quota', 'banner_used').quota },
  };
}
